import camoDesktop from 'public/assets/images/backgrounds/camo-desktop.webp';
import camoMobile from 'public/assets/images/backgrounds/camo-mobile.webp';
import signUpDesktop from 'public/assets/images/backgrounds/signup-cover-desktop.webp';
import signUpMobile from 'public/assets/images/backgrounds/signup-cover-mobile.webp';
import euLaunchDesktop from 'public/assets/images/backgrounds/eu-launch-desktop.webp';
import euLaunchMobile from 'public/assets/images/backgrounds/eu-launch-mobile.webp';
import type { GateBackdrop } from '~/components/gateBackdrop';
import { breakpoints } from '~/theme/breakpoints';

// One definition, used by the CSS swap and both preload descriptors.
// `orientation: portrait` is load-bearing: a landscape phone is under 960px wide but
// must not get the 9:16 asset, which `cover` would crop to its middle tenth.
const MOBILE_BACKDROP_MEDIA = `${breakpoints.md} and (orientation: portrait)`;

/**
 * Preload descriptors for a surface showing its backdrop to EVERY visitor. Needed
 * because this app server-renders no styled-components CSS, so the `background-image`
 * is undiscoverable until hydration finishes.
 *
 * The gated private-sale routes deliberately skip this: `links` is static per route
 * in Remix v1, so it cannot tell a gated visitor from an authenticated one, and
 * access lasts 12h — most views would fetch 220KB for an image never shown.
 */
export const preloadLinksFor = ({
  desktop,
  mobile,
  mobileMedia,
}: GateBackdrop) => [
  {
    rel: 'preload',
    as: 'image',
    fetchpriority: 'high',
    href: desktop,
    // Negation rather than a hand-written `(min-width: 961px), (orientation:
    // landscape)`: it cannot drift from the mobile condition, and the complement
    // left a gap at fractional widths between 960 and 961 in portrait where neither
    // descriptor matched. MQ3 syntax, not nested-parens MQ4 — Safari below 16.4
    // fails to parse nesting, and an unparseable `media` disables the preload.
    media: `not all and ${mobileMedia}`,
  },
  {
    rel: 'preload',
    as: 'image',
    fetchpriority: 'high',
    href: mobile,
    media: mobileMedia,
  },
];

/**
 * Camo artwork, shown on the private-sale password screens, the `/private-sale-sign-up`
 * form and `/shop`. Imported rather than path-referenced so the URLs
 * are content-hashed and a future swap can't serve a stale cached copy.
 */
export const CAMO_GATE_BACKDROP: GateBackdrop = {
  desktop: camoDesktop,
  mobile: camoMobile,
  mobileMedia: MOBILE_BACKDROP_MEDIA,
};

/**
 * `/signup`'s own artwork. `/signup` and `/shop`'s sign-up card both pass it; `/shop`
 * does not preload it (`links` is static per route and cannot see the takeover flag).
 *
 * `/signup` is in the main nav, so its preload (~234KB desktop / ~168KB mobile) is a
 * permanent cost on every visit rather than a campaign-window one.
 *
 * The scrim is 0.5, lighter than the 0.6 default, by design request. Measured against
 * THIS artwork, per pixel: the countdown's white text on bare scrimmed art is >= 4.5:1
 * on ~99.95% of pixels (worst 4.17), and `errorOnDark` on the card is >= 4.69. Swap
 * the image and those numbers are void — re-measure, or drop the override.
 */
export const SIGNUP_BACKDROP: GateBackdrop = {
  desktop: signUpDesktop,
  mobile: signUpMobile,
  mobileMedia: MOBILE_BACKDROP_MEDIA,
  scrim: 'rgba(0, 0, 0, 0.5)',
};

export const SIGNUP_PRELOAD_LINKS = preloadLinksFor(SIGNUP_BACKDROP);

/**
 * The EU warehouse launch's map. Lighter than `BACKDROP_SCRIM` because nothing but
 * outlined artwork sits on the bare backdrop — the campaign ships with no countdown.
 * Measured per pixel against the CARD (`modalBackgroundBlur` over this scrim over the
 * map): worst `mutedOnDark` 6.34:1, worst `errorOnDark` 4.65:1, and 0.5 is the lightest
 * value where the latter clears 4.5. White text on the bare scrimmed map is only
 * 3.98:1 worst case, so setting `EU_LAUNCH_COUNTDOWN_*` means dropping this override
 * first. Swap the image and re-measure (`specs/gated-campaigns.md`).
 */
export const EU_LAUNCH_BACKDROP: GateBackdrop = {
  desktop: euLaunchDesktop,
  mobile: euLaunchMobile,
  mobileMedia: MOBILE_BACKDROP_MEDIA,
  scrim: 'rgba(0, 0, 0, 0.5)',
};
